import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { LogoBankID, Null } from '@comeon/vectors';
import { H2, LabelRegular, Button } from '@comeon/ui';
import { Modal } from '~/components/modal/modal.js';
import { selectIsDesktop } from '~/components/brand/ducks/selectors.js';
import { BANKID_MODES } from '../configs';
import useTranslations from '~/components/translation/use-translations.ts';
import LoadingDots from '~/components/misc/loading-dots.js';

const BankIDContainer = styled.div`
	position: absolute;
	top: 20%;
	left: 50%;
	transform: translate(-50%, 0);
	display: flex;
	flex-direction: column;
	border-radius: 3px;
	align-items: center;
	text-align: center;
	width: 100%;
	padding: 0 24px;
`;

const LogoContainer = styled.div`
	> svg {
		width: 100px;
		height: 100px;
		.shade1,
		.shade2 {
			fill: var(--bankid-loader-title);
		}
	}
`;
const StyledH2 = styled(H2)`
	margin-top: 20px;
	color: var(--bankid-loader-title);
`;
const StyledLabelRegular = styled(LabelRegular)`
	margin-top: 10px;
	margin-bottom: 20px;
	color: var(--bankid-loader-desc);
`;
const StyledCancelButton = styled(Button)`
	font-size: 11px;
	line-height: 14px;
	text-decoration: underline;
	margin-top: 100px;
	color: var(--bankid-loader-title);
`;
const DotsContainer = styled.div`
	color: var(--bankid-loader-dots);
`;

const textKeys = [
	'BANKID_START_APP',
	'BANKID_VERIFY_YOURSELF',
	'CANCEL',
	'BANKID_VERIFICATION_PENDING_TITLE',
	'BANKID_VERIFICATION_PENDING_DESC',
	'BANKID_CANCELING'
];

const BankIDLoader = ({ onCancel, status, isWithinModal = false, mode }) => {
	const { translations } = useTranslations(textKeys);
	const isDesktop = useSelector(selectIsDesktop);
	const isRetrieveBalance = mode === BANKID_MODES.RETRIEVE_BALANCE;

	const getText = useCallback(() => {
		switch (status) {
			case 'pending':
				return {
					title: translations.BANKID_VERIFICATION_PENDING_TITLE,
					desc: translations.BANKID_VERIFICATION_PENDING_DESC
				};
			case 'canceling':
				return {
					title: translations.BANKID_CANCELING
				};
			default:
				return {
					title: translations.BANKID_START_APP,
					desc: translations.BANKID_VERIFY_YOURSELF
				};
		}
	}, [translations, status]);
	const { title, desc } = getText();
	const showCancel = status !== 'pending' && status !== 'canceling';
	return (
		<Modal
			show
			withinSidebar={!isWithinModal}
			setTopToZero
			bgColor="var(--bankid-loader-bg)"
			blockingModalView
			customHeight={isDesktop ? '100%' : '100vh'}
		>
			<BankIDContainer>
				<LogoContainer className="bankid-logo-container">
					<React.Suspense fallback={<Null />}>
						<LogoBankID />
					</React.Suspense>
				</LogoContainer>
				<StyledH2>{title}</StyledH2>
				{desc && <StyledLabelRegular>{desc}</StyledLabelRegular>}
				<DotsContainer>
					<LoadingDots />
				</DotsContainer>
				{showCancel && (
					<StyledCancelButton onClick={e => onCancel(e, isRetrieveBalance)} dataAt="bankid-loader-cancel">
						{translations.CANCEL}
					</StyledCancelButton>
				)}
			</BankIDContainer>
		</Modal>
	);
};

export default BankIDLoader;
